import { useCallback, useReducer, useRef } from 'react'
import { initialState, reducer } from './store'
import { todayIso } from './FieldOpsKit'

/**
 * Keeps the Field Ops demo alive across URLs. The seed data is deterministic, so the session only stores the LOG of
 * actions (small) in sessionStorage and replays it over the seed when any Field Ops page opens. A new browser tab, a
 * new day or the Reset button starts from the original data. Real backend: this file disappears (endpoints replace it).
 */
const KEY = 'fieldops-demo-session-v1'
const LIMIT = 3000

const readLog = () => {
    try {
        const raw = window.sessionStorage.getItem(KEY)
        if (!raw) return []
        const j = JSON.parse(raw)
        return j && j.date === todayIso() && Array.isArray(j.actions) ? j.actions : []
    } catch (e) { return [] }
}
const writeLog = (actions) => {
    try {
        if (actions.length === 0) window.sessionStorage.removeItem(KEY)
        else window.sessionStorage.setItem(KEY, JSON.stringify({ date: todayIso(), actions: actions }))
    } catch (e) { /* storage full or blocked: the demo keeps working, it just forgets on reload */ }
}

export const useDemoSession = () => {
    const log = useRef(null)
    if (log.current === null) log.current = readLog()
    const [state, rawDispatch] = useReducer(reducer, null, () => log.current.reduce((s, a) => reducer(s, a), initialState()))
    const dispatch = useCallback((action) => {
        const a = Object.assign({ ts: Date.now() }, action)
        if (a.type === 'RESET') log.current = []
        else if (log.current.length < LIMIT) log.current = log.current.concat([a])
        writeLog(log.current)
        rawDispatch(a)
    }, [])
    return [state, dispatch]
}

/** Small per-tab preferences (which admin / salesperson / driver the demo acts as). */
export const getPref = (key, fallback) => { try { return window.sessionStorage.getItem('fieldops-pref-' + key) || fallback } catch (e) { return fallback } }
export const setPref = (key, value) => { try { window.sessionStorage.setItem('fieldops-pref-' + key, value) } catch (e) { /* ignore */ } }
